import { motion } from "framer-motion";
import { fadeInUp, viewportOnce } from "@/constants";

const u = (id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1400`;

export const PHOTOS = {
  team: u("photo-1577962917302-cd874c4e31d2"),
  workshop: u("photo-1542744173-8e7e53415bb0"),
  conference: u("photo-1540575467063-178a50c2df87"),
  boardroom: u("photo-1573164574572-cb89e39749b4"),
  labTeacher: u("photo-1778489769184-45868633c527"),
  studentsLaptop: u("photo-1653503425441-9d975e51ce91"),
  lecture: u("photo-1758270704262-ecc82b23dc37"),
  lectureHall: u("photo-1606761568499-6d2451b23c66"),
  code: u("photo-1607799279861-4dd421887fb3"),
  chip: u("photo-1518770660439-4636190af475"),
  circuit: u("photo-1592659762303-90081d34b277"),
  iot: u("photo-1677092419414-e974582c492c"),
  security: u("photo-1614064548237-096f735f344f"),
  cables: u("photo-1744868562210-fffb7fa882d9"),
  graduation: u("photo-1541339907198-e08756dedf3f"),
  mentor: u("photo-1573497620053-ea5300f94f21"),
  msme: u("photo-1543269664-56d93c1b41a6"),
  skyline: u("photo-1758919917007-100ed47d1e4f"),
  sales: u("photo-1517048676732-d65bc937f952"),
  strategy: u("photo-1532619675605-1ede6c2ed2b0"),
  genai: "https://images.pexels.com/photos/8386437/pexels-photo-8386437.jpeg?auto=compress&cs=tinysrgb&w=1400",
};

export const FramedPhoto = ({ src, alt, badge, aspect = "aspect-[4/3]", className = "", testId }) => (
  <motion.div variants={fadeInUp} className={`relative ${className}`} data-testid={testId}>
    <div className="absolute -top-4 -right-4 w-2/3 h-2/3 rounded-2xl bg-[#C9A962]/15 border border-[#C9A962]/25" />
    <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
      <img src={src} alt={alt} loading="lazy" className={`w-full ${aspect} object-cover transition-transform duration-700 group-hover:scale-105`} />
    </div>
    {badge && (
      <div className="absolute -bottom-5 left-5 bg-[#C9A962] text-[#0A1628] rounded-xl px-5 py-3 shadow-xl">
        <p className="text-xl font-bold leading-none">{badge.value}</p>
        <p className="text-xs font-medium mt-1">{badge.label}</p>
      </div>
    )}
  </motion.div>
);

export const PhotoBand = ({ src, eyebrow, title, text, testId }) => (
  <section className="relative h-[340px] md:h-[420px] overflow-hidden" data-testid={testId}>
    <motion.img
      src={src}
      alt={title}
      loading="lazy"
      initial={{ scale: 1.15 }}
      whileInView={{ scale: 1 }}
      viewport={viewportOnce}
      transition={{ duration: 1.8, ease: "easeOut" }}
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/70 to-[#0A1628]/20" />
    <div className="container-custom relative h-full flex items-center">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.7 }} className="max-w-xl">
        {eyebrow && <p className="text-[#C9A962] text-xs font-semibold uppercase tracking-[0.25em] mb-4">{eyebrow}</p>}
        <h3 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">{title}</h3>
        {text && <p className="text-[#D0D6E0] text-base md:text-lg">{text}</p>}
      </motion.div>
    </div>
  </section>
);

export const CardImage = ({ src, alt, pad = "p-8" }) => {
  const bleed = pad === "p-6" ? "-mx-6 -mt-6" : pad === "p-6 md:p-8" ? "-mx-6 -mt-6 md:-mx-8 md:-mt-8" : "-mx-8 -mt-8";
  return (
    <div className={`${bleed} mb-6 overflow-hidden rounded-t-lg aspect-[16/9]`}>
      <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
    </div>
  );
};

export const PhotoMosaic = ({ items, testId }) => (
  <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid={testId}>
    {items.map((it, i) => (
      <motion.div
        key={it.src}
        variants={fadeInUp}
        transition={{ delay: i * 0.1 }}
        className={`keep-dark relative overflow-hidden rounded-xl group ${i === 0 ? "col-span-2 row-span-2" : ""}`}
      >
        <img src={it.src} alt={it.label} loading="lazy" className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${i === 0 ? "aspect-square md:aspect-auto" : "aspect-[4/3]"}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 via-transparent to-transparent" />
        <p className="absolute bottom-3 left-4 right-4 text-white text-sm font-semibold">{it.label}</p>
      </motion.div>
    ))}
  </motion.div>
);
